import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../layout/PageHeader";
import { FiArrowLeft, FiEdit2, FiX } from "react-icons/fi";

/**
 * CrudDetailPage - View page showing item details with edit mode toggle
 *
 * @param {string} title - Page title
 * @param {string} backLink - URL to return to list
 * @param {string} backLabel - Label for back button
 * @param {array} breadcrumb - Breadcrumb items [{label, to}]
 * @param {node} detailContent - Main detail view content
 * @param {node} editForm - Edit form content (shown when editing)
 * @param {function} onSave - Save handler for edit form
 * @param {boolean} isLoading - Loading state
 * @param {node} children - Related sections (tables, etc.)
 * @param {string} variant - Style variant: "default" or "v4"
 * @param {node} icon - Icon element for V4 header
 */
const CrudDetailPage = ({
	title,
	backLink,
	backLabel = "Back",
	breadcrumb = [],
	detailContent,
	editForm,
	onSave,
	isLoading = false,
	children,
	variant = "default",
	icon,
}) => {
	// Default to edit mode if there's no detail content (i.e., new items)
	const [isEditing, setIsEditing] = useState(!detailContent);

	const handleSave = async (e) => {
		e.preventDefault();
		if (onSave) {
			await onSave(e);
			setIsEditing(false);
		}
	};

	const headerActions = (
		<div style={{ display: "flex", gap: "8px" }}>
			{!isEditing && editForm && (
				<button className="btn btn--primary" onClick={() => setIsEditing(true)}>
					<FiEdit2 style={{ marginRight: 6 }} />
					Edit
				</button>
			)}
			{backLink && (
				<Link to={backLink} className="btn btn--secondary">
					<FiArrowLeft style={{ marginRight: 6 }} />
					{backLabel}
				</Link>
			)}
		</div>
	);

	const pageClasses = [
		"crud-page",
		"crud-detail",
		variant === "v4" && "crud-page--v4",
	].filter(Boolean).join(" ");

	const isV4 = variant === "v4";

	return (
		<div className={pageClasses}>
			<PageHeader title={title} breadcrumb={isV4 ? [] : breadcrumb} actions={headerActions} v4={isV4} icon={icon} />

			{/* Edit Mode */}
			{isEditing && editForm && (
				<div className="crud-detail__edit-panel">
					<div className="crud-detail__edit-header">
						<h3>Edit Details</h3>
						<button className="btn btn--ghost btn--sm" onClick={() => setIsEditing(false)}>
							<FiX /> Cancel
						</button>
					</div>
					<form onSubmit={handleSave}>
						{editForm}
						<div className="crud-detail__edit-actions">
							<button type="submit" className="btn btn--primary" disabled={isLoading}>
								{isLoading ? "Saving..." : "Save Changes"}
							</button>
						</div>
					</form>
				</div>
			)}

			{/* Detail View */}
			{!isEditing && detailContent && (
				<div className="crud-detail__content">{detailContent}</div>
			)}

			{/* Related Sections */}
			{children && <div className="crud-detail__related">{children}</div>}
		</div>
	);
};

/**
 * DetailSection - A titled section for grouping related fields
 */
export const DetailSection = ({ title, children }) => (
	<div className="crud-detail__section">
		{title && <h3 className="crud-detail__section-title">{title}</h3>}
		<div className="crud-detail__section-content">{children}</div>
	</div>
);

/**
 * DetailField - A single field with label and value
 */
export const DetailField = ({ label, value, link }) => (
	<div className="crud-detail__field">
		<span className="crud-detail__field-label">{label}</span>
		{link ? (
			<Link to={link} className="crud-detail__field-value crud-detail__field-value--link">
				{value || "—"}
			</Link>
		) : (
			<span className="crud-detail__field-value">{value || "—"}</span>
		)}
	</div>
);

/**
 * DetailGrid - Grid layout for fields
 */
export const DetailGrid = ({ children }) => (
	<div className="crud-detail__grid">{children}</div>
);

/**
 * RelatedSection - Section for related items table
 */
export const RelatedSection = ({ title, count, addLink, addLabel, children }) => (
	<div className="crud-detail__related-section">
		<div className="crud-detail__related-header">
			<h3 className="crud-detail__related-title">
				{title} {count !== undefined && <span className="crud-detail__count">({count})</span>}
			</h3>
			{addLink && (
				<Link to={addLink} className="btn btn--secondary btn--sm">
					+ {addLabel || "Add"}
				</Link>
			)}
		</div>
		{children}
	</div>
);

export default CrudDetailPage;
